/**
 * Registering an animal (BR-12, P2-BR3, P2-TR3).
 *
 * The plan's P2 deliverable was "add purchased/born animals, photos, tags",
 * and until now the only route into the herd was the purchase-trip wizard. A
 * calf born on the farm therefore could not be recorded at all, and no animal
 * could be given an age or a mother, even though `POST /animals` has accepted
 * both since the schema was written.
 *
 * Source is the first question and everything else follows from it, because
 * the two animals are genuinely different records. A purchased animal has a
 * date and a price and a mother who is somebody else's goat. A farm-born one
 * has a mother in this herd, no price at all, and is usually being entered on
 * the day it was born. Asking source first means a partner recording a calving
 * is never shown a price field, which is the field the server would reject.
 *
 * Age is asked in months and converted to `dob_estimate` here (see
 * `animalAge.ts` for why). The payload that leaves this form contains no age
 * field — the server's schema is `.strict()` and would refuse one.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorFields, errorMessageKey } from '../../lib/apiError.js';
import { todayInDhaka } from '../../lib/date.js';
import { MAX_AGE_MONTHS, dobFromAgeMonths } from './animalAge.js';
import { useAnimals, useCreateAnimal, useNextTag, type CreateAnimalInput } from './api.js';

/** Digits with at most two decimals, checked as text — never parsed (MONEY-1). */
const DECIMAL_DRAFT = /^\d{1,12}(\.\d{1,2})?$/;

const SPECIES = ['goat', 'cattle'] as const;
const SEXES = ['female', 'male'] as const;
const SOURCES = ['purchased', 'born'] as const;

type Species = (typeof SPECIES)[number];
type Sex = (typeof SEXES)[number];
type Source = (typeof SOURCES)[number];

export function AnimalForm(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateAnimal();

  const [source, setSource] = useState<Source>('purchased');
  const [species, setSpecies] = useState<Species>('goat');
  const [sex, setSex] = useState<Sex>('female');
  const [tag, setTag] = useState('');
  const [breed, setBreed] = useState('');
  const [ageMonths, setAgeMonths] = useState('');
  const [motherId, setMotherId] = useState<number | null>(null);
  const [purchaseDate, setPurchaseDate] = useState(todayInDhaka());
  const [purchasePrice, setPurchasePrice] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { data: nextTag } = useNextTag(species);

  /*
   * Only the animals that could actually be a mother.
   *
   * The server checks all three of these and returns a readable message, but a
   * picker that offers a male goat as the mother of a cow is a picker that
   * invites the error. Filtering here means the list is short enough to scan
   * on a phone, which matters more than it sounds: a partner scrolling forty
   * tags to find one picks the wrong one.
   */
  const { data: herd } = useAnimals({ species, sex: 'female', status: 'active', limit: 100 });
  const mothers = herd?.items ?? [];

  /*
   * An age that is typed but unusable must block the save rather than be
   * dropped. Sending the animal with no birth date because "abc" could not be
   * parsed would record a fact nobody stated.
   */
  const dobEstimate = dobFromAgeMonths(ageMonths);
  const ageIsUsable = ageMonths.trim() === '' || dobEstimate !== null;

  const priceIsUsable = source === 'born' || DECIMAL_DRAFT.test(purchasePrice);
  const canSubmit = ageIsUsable && priceIsUsable && !create.isPending;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrorKey(null);
    setFieldErrors({});

    // Built key by key rather than spread-and-delete: the server's schema is
    // .strict(), so an `undefined` left on the object is still a key on the
    // wire and still a 422.
    const input: CreateAnimalInput = {
      species,
      sex,
      source,
      ...(tag.trim() !== '' ? { tag: tag.trim().toUpperCase() } : {}),
      ...(breed.trim() !== '' ? { breed: breed.trim() } : {}),
      ...(dobEstimate !== null ? { dobEstimate } : {}),
      ...(motherId !== null ? { motherId } : {}),
      // BR-12 — a farm-born animal has no purchase price, and the server
      // refuses one outright. The fields are hidden above; this is the guard
      // that survives a state left over from switching source mid-form.
      ...(source === 'purchased' ? { purchaseDate, purchasePrice } : {}),
    };

    try {
      const animal = await create.mutateAsync(input);
      navigate(`/animals/${String(animal.id)}`);
    } catch (error) {
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('animals.add.title')}</h1>

      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <fieldset className="mb-4">
          <legend className="mb-2 text-sm font-medium text-gray-700">{t('animals.add.source')}</legend>
          <div className="grid grid-cols-2 gap-2">
            {SOURCES.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setSource(option)}
                aria-pressed={source === option}
                className={`min-h-touch rounded-xl px-3 py-3 text-sm font-medium ${
                  source === option ? 'bg-green-700 text-white' : 'bg-white text-gray-700 shadow-sm'
                }`}
              >
                {t(`animals.source.${option}`)}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="animal-species">
              {t('animals.speciesLabel')}
            </label>
            <select
              id="animal-species"
              value={species}
              onChange={(event) => {
                setSpecies(event.target.value as Species);
                // A mother of the old species cannot be the mother of the new
                // one, and leaving the id selected would send a pairing the
                // server refuses.
                setMotherId(null);
              }}
              className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3"
            >
              {SPECIES.map((option) => (
                <option key={option} value={option}>
                  {t(`animals.species.${option}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="animal-sex">
              {t('animals.sexLabel')}
            </label>
            <select
              id="animal-sex"
              value={sex}
              onChange={(event) => setSex(event.target.value as Sex)}
              className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3"
            >
              {SEXES.map((option) => (
                <option key={option} value={option}>
                  {t(`animals.sex.${option}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label className="mb-1 mt-3 block text-sm font-medium text-gray-700" htmlFor="animal-tag">
          {t('animals.add.tag')}
        </label>
        <input
          id="animal-tag"
          value={tag}
          onChange={(event) => setTag(event.target.value)}
          // The suggestion as a placeholder, not as a value: leaving the box
          // empty is what asks the server for the next tag, and pre-filling it
          // would race a second partner registering an animal at the same time
          // (P2-BR1).
          placeholder={nextTag?.tag ?? ''}
          className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3 uppercase"
        />
        <p className="mt-1 text-xs text-gray-500">{t('animals.add.tagHint')}</p>
        {fieldErrors['tag'] !== undefined && <p className="mt-1 text-sm text-red-700">{fieldErrors['tag']}</p>}

        <label className="mb-1 mt-3 block text-sm font-medium text-gray-700" htmlFor="animal-breed">
          {t('animals.add.breed')}
        </label>
        <input
          id="animal-breed"
          value={breed}
          onChange={(event) => setBreed(event.target.value)}
          className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3"
        />

        {/* Age, in the unit a partner actually knows it in. */}
        <label className="mb-1 mt-3 block text-sm font-medium text-gray-700" htmlFor="animal-age">
          {t('animals.add.ageMonths')}
        </label>
        <input
          id="animal-age"
          inputMode="numeric"
          value={ageMonths}
          onChange={(event) => setAgeMonths(event.target.value)}
          max={MAX_AGE_MONTHS}
          placeholder="0"
          className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3 tabular-nums"
        />
        <p className="mt-1 text-xs text-gray-500" aria-live="polite">
          {ageMonths.trim() === ''
            ? t('animals.add.ageHint')
            : dobEstimate !== null
              ? t('animals.add.ageDerived', { date: dobEstimate })
              : t('animals.add.ageInvalid')}
        </p>
        {fieldErrors['dobEstimate'] !== undefined && (
          <p className="mt-1 text-sm text-red-700">{fieldErrors['dobEstimate']}</p>
        )}

        {/* BR-12 — the mother, when she is in this herd. */}
        <label className="mb-1 mt-3 block text-sm font-medium text-gray-700" htmlFor="animal-mother">
          {t(source === 'born' ? 'animals.add.motherRequired' : 'animals.add.mother')}
        </label>
        <select
          id="animal-mother"
          value={motherId === null ? '' : String(motherId)}
          onChange={(event) => setMotherId(event.target.value === '' ? null : Number(event.target.value))}
          className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3"
        >
          <option value="">{t('animals.add.motherNone')}</option>
          {mothers.map((mother) => (
            <option key={mother.id} value={mother.id}>
              {mother.tag}
              {mother.breed !== null ? ` · ${mother.breed}` : ''}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          {mothers.length === 0
            ? t('animals.add.motherEmpty')
            : t(source === 'born' ? 'animals.add.motherHintBorn' : 'animals.add.motherHint')}
        </p>
        {fieldErrors['motherId'] !== undefined && (
          <p className="mt-1 text-sm text-red-700">{fieldErrors['motherId']}</p>
        )}

        {source === 'purchased' ? (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="animal-purchase-date">
                {t('animals.add.purchaseDate')}
              </label>
              <input
                id="animal-purchase-date"
                type="date"
                value={purchaseDate}
                max={todayInDhaka()}
                onChange={(event) => setPurchaseDate(event.target.value)}
                className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="animal-purchase-price">
                {t('animals.add.purchasePrice')}
              </label>
              <input
                id="animal-purchase-price"
                inputMode="decimal"
                value={purchasePrice}
                onChange={(event) => setPurchasePrice(event.target.value)}
                placeholder="0.00"
                className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3 tabular-nums"
              />
            </div>
          </div>
        ) : (
          // Said plainly rather than left as an absence: a partner who expects
          // a price field needs to know it is missing on purpose.
          <p className="mt-3 rounded-lg bg-gray-100 px-4 py-3 text-xs text-gray-600">{t('animals.add.bornNote')}</p>
        )}

        {source === 'purchased' && (
          // This registers the animal; it does not spend anything. The trip
          // wizard is what names an account and posts the expense, and a
          // partner who thinks this did both would go looking for a
          // transaction that was never created (BR-05).
          <p className="mt-3 rounded-lg bg-amber-50 px-4 py-3 text-xs text-amber-900">
            {t('animals.add.noExpenseNote')}
          </p>
        )}
        {fieldErrors['purchasePrice'] !== undefined && (
          <p className="mt-1 text-sm text-red-700">{fieldErrors['purchasePrice']}</p>
        )}
        {fieldErrors['purchaseDate'] !== undefined && (
          <p className="mt-1 text-sm text-red-700">{fieldErrors['purchaseDate']}</p>
        )}

        {errorKey !== null && (
          <div role="alert" className="mt-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
            <p>{t(errorKey)}</p>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <button
            type="submit"
            disabled={!canSubmit}
            className="min-h-[52px] flex-1 rounded-xl bg-green-700 text-base font-medium text-white disabled:opacity-50"
          >
            {t(create.isPending ? 'animals.add.saving' : 'animals.add.submit')}
          </button>
          <button
            type="button"
            onClick={() => void navigate('/animals')}
            className="min-h-touch rounded-xl bg-white px-4 py-2 text-sm text-gray-700"
          >
            {t('app.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
